import { z } from 'zod';
import { distance, EPS, isSimplePolygon, polygonArea } from '@indusketch/geometry';
import {
  annotationSchema,
  backgroundSchema,
  levelBackgroundSchema,
  buildingSchema,
  columnSchema,
  fenceSchema,
  gateSchema,
  canopySchema,
  measurementSchema,
  mezzanineSchema,
  railingSchema,
  parkingSchema,
  roadSchema,
  roofElementSchema,
  openingSchema,
  placedObjectSchema,
  roomSchema,
  siteSchema,
  stairSchema,
  wallImageSchema,
  wallSchema,
  zoneSchema,
} from './entities.js';
import { idSchema, nonNegative, positive } from './primitives.js';
import { modelTexts, type ModelTexts } from '../texts.js';

export const CURRENT_SCHEMA_VERSION = 26 as const;

export const displayUnitSchema = z.enum(['mm', 'cm', 'm']);
export const roofModeSchema = z.enum(['visible', 'transparent', 'hidden']);

const percent = z.number().min(0).max(100);

/** Unit prices and surcharges of the rough cost estimate; missing values use the defaults. */
export const costSettingsSchema = z.object({
  /** Price catalog the unit prices start from; missing means the installation's default. */
  catalogId: idSchema.optional(),
  /** € per unit of each cost item (by item id), over the catalog's. */
  prices: z.record(z.string().max(40), z.number().min(0).max(1e7)).optional(),
  /** Added to the steel weight for connections, plates and bolts. */
  steelExtra: percent.optional(),
  /** Not measured from the plan: percent of the measured items. */
  foundations: percent.optional(),
  /** Footings under the columns and walls worked out from their loads instead of a percent. */
  foundationsFromColumns: z.boolean().optional(),
  services: percent.optional(),
  fees: percent.optional(),
  /** The total is shown as a range of ± this many percent. */
  range: z.number().min(0).max(50).optional(),
});

export const loadCountrySchema = z.enum(['de', 'pl']);
/** Snow zones: Germany 1, 1a, 2, 2a, 3; Poland 1 to 5. */
export const snowZoneSchema = z.enum(['1', '1a', '2', '2a', '3', '4', '5']);
/** Wind zones: Germany 1 to 4; Poland 1 to 3. */
export const windZoneSchema = z.enum(['1', '2', '3', '4']);
/** Germany: inland or coast; Poland: terrain category II (open), III (suburban), IV (urban). */
export const terrainSchema = z.enum(['inland', 'coast', 'open', 'suburban', 'urban']);

const COUNTRY_ZONES = {
  de: {
    snow: ['1', '1a', '2', '2a', '3'],
    wind: ['1', '2', '3', '4'],
    terrain: ['inland', 'coast'],
  },
  pl: {
    snow: ['1', '2', '3', '4', '5'],
    wind: ['1', '2', '3'],
    terrain: ['open', 'suburban', 'urban'],
  },
} as const;

/** Site loads for the pre-sizing; missing means the usual snow and wind of the tables. */
export const loadSettingsSchema = z
  .object({
    /** Country whose national annex the snow and wind maps follow; missing means Germany. */
    country: loadCountrySchema.optional(),
    /** Snow load zone of EN 1991-1-3 and the country's national annex. */
    snowZone: snowZoneSchema,
    /** Height of the site above sea level in metres. */
    altitude: z.number().min(0).max(1500),
    /** Wind zone of EN 1991-1-4; missing means zone 2 (Germany) or 1 (Poland). */
    windZone: windZoneSchema.optional(),
    terrain: terrainSchema.optional(),
  })
  .superRefine((v, ctx) => {
    const zones = COUNTRY_ZONES[v.country ?? 'de'];
    const check = (ok: boolean, path: string) => {
      if (!ok) ctx.addIssue({ code: 'custom', path: [path], message: 'Zone of another country' });
    };
    check((zones.snow as readonly string[]).includes(v.snowZone), 'snowZone');
    if (v.windZone) check((zones.wind as readonly string[]).includes(v.windZone), 'windZone');
    if (v.terrain) check((zones.terrain as readonly string[]).includes(v.terrain), 'terrain');
  });

export const settingsSchema = z.object({
  displayUnit: displayUnitSchema,
  grid: z.object({ size: positive, visible: z.boolean() }),
  snapping: z.object({
    enabled: z.boolean(),
    grid: z.boolean(),
    geometry: z.boolean(),
    tolerance: nonNegative,
  }),
  collisionCheck: z.boolean(),
  roofMode: roofModeSchema,
  wallsTransparent: z.boolean(),
  /** Steel frames of the halls in 3D; missing means shown. */
  structureVisible: z.boolean().optional(),
  /** Rafters, purlins, roof bracing and haunches in 3D (columns stay); missing means shown. */
  structureRoof: z.boolean().optional(),
  costs: costSettingsSchema.optional(),
  loads: loadSettingsSchema.optional(),
  /** Inside a building, grid, snapping and new elements follow its walls instead of the plan. */
  alignToBuilding: z.boolean().default(true),
});

const entityMap = <T extends z.ZodType>(schema: T) => z.record(idSchema, schema);

const documentShape = z.object({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
  id: idSchema,
  name: z.string().trim().min(1).max(200),
  description: z.string().max(10000),
  settings: settingsSchema,
  /** Land parcels (Flurstücke); adjacent parcels form the overall site. */
  sites: entityMap(siteSchema),
  buildings: entityMap(buildingSchema),
  walls: entityMap(wallSchema),
  openings: entityMap(openingSchema),
  zones: entityMap(zoneSchema),
  rooms: entityMap(roomSchema),
  objects: entityMap(placedObjectSchema),
  measurements: entityMap(measurementSchema),
  annotations: entityMap(annotationSchema),
  roofElements: entityMap(roofElementSchema),
  roads: entityMap(roadSchema),
  parkings: entityMap(parkingSchema),
  fences: entityMap(fenceSchema),
  gates: entityMap(gateSchema),
  stairs: entityMap(stairSchema),
  mezzanines: entityMap(mezzanineSchema),
  canopies: entityMap(canopySchema),
  railings: entityMap(railingSchema),
  columns: entityMap(columnSchema),
  /** Pictures and logos on walls. */
  wallImages: entityMap(wallImageSchema),
  /** Plan of the ground floor (also lying on the ground in 3D). */
  background: backgroundSchema.nullable(),
  /** Plans of the upper floors, one per floor. */
  levelBackgrounds: z.array(levelBackgroundSchema).max(50).optional(),
});

type DocumentShape = z.infer<typeof documentShape>;

const ENTITY_COLLECTIONS = [
  'sites',
  'buildings',
  'walls',
  'openings',
  'zones',
  'rooms',
  'objects',
  'measurements',
  'annotations',
  'roofElements',
  'roads',
  'parkings',
  'fences',
  'gates',
  'stairs',
  'mezzanines',
  'canopies',
  'railings',
  'columns',
  'wallImages',
] as const;

/**
 * Cross-entity integrity rules. They live here rather than in the UI so that the server,
 * migrations and import paths enforce exactly the same invariants.
 */
function checkIntegrity(doc: DocumentShape, ctx: z.RefinementCtx): void {
  const issue = (path: (string | number)[], key: keyof ModelTexts['issues']) => {
    ctx.addIssue({ code: 'custom', path, message: modelTexts().issues[key] });
  };

  const seen = new Set<string>();
  for (const collection of ENTITY_COLLECTIONS) {
    const entities: Record<string, { id: string }> = doc[collection];
    for (const [key, entity] of Object.entries(entities)) {
      if (entity.id !== key) issue([collection, key, 'id'], 'idMismatch');
      if (seen.has(entity.id)) issue([collection, key, 'id'], 'idDuplicate');
      seen.add(entity.id);
    }
  }

  for (const [key, s] of Object.entries(doc.sites)) {
    if (!isSimplePolygon(s.boundary)) issue(['sites', key, 'boundary'], 'siteBoundary');
  }

  for (const [key, b] of Object.entries(doc.buildings)) {
    if (b.wallThickness * 2 >= Math.min(b.width, b.depth)) {
      issue(['buildings', key, 'wallThickness'], 'wallTooThick');
    }
    if (b.footprint) {
      if (!isSimplePolygon(b.footprint) || polygonArea(b.footprint) < 4)
        issue(['buildings', key, 'footprint'], 'hallOutline');
      if (b.roofType === 'gable') issue(['buildings', key, 'roofType'], 'hallOutlineRoof');
      if (b.eaveEdge !== null && b.eaveEdge >= b.footprint.length)
        issue(['buildings', key, 'eaveEdge'], 'hallOutline');
    }
    if ((b.kind === 'office') !== (b.office !== null)) {
      issue(['buildings', key, 'office'], 'officeData');
    }
    if (b.office) {
      const total = b.office.floors.reduce(
        (s, f) => s + f.clearHeight + (b.office?.slabThickness ?? 0),
        0,
      );
      if (Math.abs(total - b.eaveHeight) > 1e-6)
        issue(['buildings', key, 'eaveHeight'], 'officeHeight');
    }
  }

  const mezzanineHalls = new Set(Object.values(doc.mezzanines).map((m) => m.buildingId));
  for (const [key, m] of Object.entries(doc.mezzanines)) {
    const b = doc.buildings[m.buildingId];
    if (b?.kind !== 'hall') {
      issue(['mezzanines', key, 'buildingId'], 'mezzanineNoHall');
      continue;
    }
    if (!isSimplePolygon(m.polygon) || polygonArea(m.polygon) < 1)
      issue(['mezzanines', key, 'polygon'], 'mezzaninePolygon');
    if (m.elevation < m.slabThickness || m.elevation > b.eaveHeight - 2 + EPS)
      issue(['mezzanines', key, 'elevation'], 'mezzanineHeight');
  }

  for (const [key, c] of Object.entries(doc.columns)) {
    const b = doc.buildings[c.buildingId];
    if (!b) issue(['columns', key, 'buildingId'], 'columnNoBuilding');
    else if (b.office && c.level >= b.office.floors.length)
      issue(['columns', key, 'level'], 'columnNoBuilding');
  }

  for (const [key, s] of Object.entries(doc.stairs)) {
    const b = doc.buildings[s.buildingId];
    if (b?.kind === 'hall' && (mezzanineHalls.has(b.id) || s.toRoof !== undefined)) {
      if (s.fromLevel !== 0) issue(['stairs', key, 'fromLevel'], 'noUpperLevel');
      continue;
    }
    if (!b?.office) {
      issue(['stairs', key, 'buildingId'], 'stairNoOffice');
      continue;
    }
    const top =
      b.office.floors.length -
      1 +
      (b.office.placement === 'indoor' && b.office.usableTop ? 1 : 0) +
      (s.toRoof !== undefined ? 1 : 0);
    if (s.fromLevel + 1 > top) issue(['stairs', key, 'fromLevel'], 'noUpperLevel');
  }

  for (const [key, r] of Object.entries(doc.roofElements)) {
    const b = doc.buildings[r.buildingId];
    if (!b) {
      issue(['roofElements', key, 'buildingId'], 'roofElementNoHall');
      continue;
    }
    const c = Math.abs(Math.cos(r.rotation));
    const s = Math.abs(Math.sin(r.rotation));
    const hu = (c * r.width + s * r.depth) / 2;
    const hv = (s * r.width + c * r.depth) / 2;
    if (Math.abs(r.u) + hu > b.width / 2 + EPS || Math.abs(r.v) + hv > b.depth / 2 + EPS) {
      issue(['roofElements', key], 'roofElementOutside');
    }
  }
  const checkPolyline = (
    collection: 'roads' | 'fences' | 'railings',
    key: string,
    points: readonly { x: number; y: number }[],
  ) => {
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const p = points[i];
      if (a && p && distance(a, p) < EPS) issue([collection, key, 'points'], 'duplicatePoints');
    }
  };
  for (const [key, r] of Object.entries(doc.roads)) checkPolyline('roads', key, r.points);
  for (const [key, f] of Object.entries(doc.fences)) checkPolyline('fences', key, f.points);
  for (const [key, r] of Object.entries(doc.railings)) {
    checkPolyline('railings', key, r.points);
    if (r.mount === 'roof' && !(r.buildingId && doc.buildings[r.buildingId]))
      issue(['railings', key, 'buildingId'], 'railingRoof');
  }

  for (const [key, c] of Object.entries(doc.canopies)) {
    const wall = doc.walls[c.wallId];
    if (!wall) {
      issue(['canopies', key, 'wallId'], 'canopyWall');
      continue;
    }
    if (c.offsetFromWallStart + c.width > distance(wall.start, wall.end) + EPS)
      issue(['canopies', key], 'canopyPastEnd');
  }

  for (const [key, p] of Object.entries(doc.wallImages)) {
    if (!doc.walls[p.wallId]) issue(['wallImages', key, 'wallId'], 'wallImageWall');
  }

  for (const [key, wall] of Object.entries(doc.walls)) {
    if (distance(wall.start, wall.end) < EPS) issue(['walls', key], 'wallLength');
    if (
      wall.ownerId !== null &&
      !doc.buildings[wall.ownerId] &&
      !doc.rooms[wall.ownerId] &&
      !doc.stairs[wall.ownerId]
    ) {
      issue(['walls', key, 'ownerId'], 'wallOwner');
    }
  }

  for (const [key, opening] of Object.entries(doc.openings)) {
    const wall = doc.walls[opening.wallId];
    if (!wall) {
      issue(['openings', key, 'wallId'], 'openingWall');
      continue;
    }
    const wallLength = distance(wall.start, wall.end);
    if (opening.offsetFromWallStart + opening.width > wallLength + EPS) {
      issue(['openings', key], 'openingPastEnd');
    }
    if (opening.sillHeight + opening.height > wall.height + EPS) {
      issue(['openings', key], 'openingTooHigh');
    }
  }

  // Openings may sit above each other (e.g. windows on two floors), but must not overlap.
  const byWall = new Map<
    string,
    { key: string; from: number; to: number; bottom: number; top: number }[]
  >();
  for (const [key, o] of Object.entries(doc.openings)) {
    const list = byWall.get(o.wallId) ?? [];
    list.push({
      key,
      from: o.offsetFromWallStart,
      to: o.offsetFromWallStart + o.width,
      bottom: o.sillHeight,
      top: o.sillHeight + o.height,
    });
    byWall.set(o.wallId, list);
  }
  for (const list of byWall.values()) {
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i];
        const b = list[j];
        if (
          a &&
          b &&
          a.from < b.to - EPS &&
          b.from < a.to - EPS &&
          a.bottom < b.top - EPS &&
          b.bottom < a.top - EPS
        ) {
          issue(['openings', b.key], 'openingsOverlap');
        }
      }
    }
  }

  const checkBuildingRef = (
    collection: 'zones' | 'rooms',
    key: string,
    buildingId: string | null,
  ) => {
    if (buildingId !== null && !doc.buildings[buildingId]) {
      issue([collection, key, 'buildingId'], 'unknownHall');
    }
  };
  for (const [key, zone] of Object.entries(doc.zones)) {
    checkBuildingRef('zones', key, zone.buildingId);
    if (!isSimplePolygon(zone.polygon)) issue(['zones', key, 'polygon'], 'zonePolygon');
  }
  for (const [key, room] of Object.entries(doc.rooms)) {
    checkBuildingRef('rooms', key, room.buildingId);
    if (!isSimplePolygon(room.polygon)) issue(['rooms', key, 'polygon'], 'roomPolygon');
  }
}

export const projectDocumentSchema = documentShape.superRefine(checkIntegrity);

export type ProjectDocument = z.infer<typeof projectDocumentSchema>;
export type ProjectSettings = z.infer<typeof settingsSchema>;
export type EntityCollection = (typeof ENTITY_COLLECTIONS)[number];
export { ENTITY_COLLECTIONS };
